import { useState, useEffect } from 'react';
import { AuthorizedUser } from '../models/User';
import { safeLocalStorage } from '../utils/storage';

interface UseDisclaimerReturn {
  showDisclaimer: boolean;
  acceptDisclaimer: () => void;
}

const CONSENT_GRANTED = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
} as const;

/**
 * Signal Google Consent Mode v2 granted state.
 * Safe to call even when gtag.js has not yet loaded — commands are queued.
 */
function signalConsentGranted(): void {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', CONSENT_GRANTED);
  }
}

/**
 * Marks the disclaimer as accepted (persisted in localStorage) and fires the
 * Consent Mode v2 grant signal. Exported so signup flows on landing pages can
 * record acceptance programmatically: the signup form's `auth.tos_agree`
 * checkbox covers the same three bullets (18+, cookies, T&C/privacy) so we
 * don't need to re-prompt with the standalone modal afterwards.
 */
export function markDisclaimerAccepted(): void {
  try {
    safeLocalStorage.setItem('disclaimerAccepted', 'true');
  } catch {
    // localStorage may be unavailable (private mode, etc.) -- silently ignore
  }
  signalConsentGranted();
  window.dispatchEvent(new Event(DISCLAIMER_ACCEPTED_EVENT));
}

/** Broadcast by markDisclaimerAccepted(), so modals waiting for the disclaimer can open. */
export const DISCLAIMER_ACCEPTED_EVENT = 'app:disclaimer-accepted';

// Same storage as useDisclaimer, so both agree when localStorage is unavailable
const isDisclaimerAccepted = (): boolean => safeLocalStorage.getItem('disclaimerAccepted') !== null;

/**
 * Whether a guest has accepted the disclaimer (members never see it), turning
 * true when they do: a modal opened on page load (home's `?login=1` /
 * `?signup=1`) waits for it instead of opening on top of the disclaimer.
 */
export const useDisclaimerAccepted = (user: AuthorizedUser | undefined): boolean => {
  const [accepted, setAccepted] = useState(isDisclaimerAccepted);

  useEffect(() => {
    if (accepted) {
      return;
    }
    const onAccepted = () => setAccepted(true);
    window.addEventListener(DISCLAIMER_ACCEPTED_EVENT, onAccepted);
    return () => window.removeEventListener(DISCLAIMER_ACCEPTED_EVENT, onAccepted);
  }, [accepted]);

  return accepted || !!user;
};

export const useDisclaimer = (user: AuthorizedUser | undefined): UseDisclaimerReturn => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    // Logged-in users never see the disclaimer. If it was previously queued
    // for a guest in this render tree, hide it again now that we have a user
    // (e.g. they just signed up via a landing page form).
    if (user) {
      setShowDisclaimer(false);
      return;
    }

    const disclaimerAccepted = safeLocalStorage.getItem('disclaimerAccepted');
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
    } else {
      // Returning visitor — re-signal consent for this page load.
      // consent.php handles this for the PHP disclaimer cookie, but the React
      // DisclaimerModal uses localStorage so we must signal it here too.
      signalConsentGranted();
    }
  }, [user]);

  const acceptDisclaimer = () => {
    markDisclaimerAccepted();
    setShowDisclaimer(false);
  };

  return {
    showDisclaimer,
    acceptDisclaimer
  };
};